<template lang="pug">
v-form(v-model="isFormValid" ref="form" lazy-validation :disabled="isRequestInProgress" @submit.prevent="onFormSubmit")
    v-card.card-envelope(flat)
        v-card-title {{$t('passwordRecovery.request.title')}}

        v-card-subtitle.mt-1(v-if="showTextInstructions")
            p {{$t('passwordRecovery.request.message1')}}
            p {{$t('passwordRecovery.request.message2')}}
              a.pl-1(:href="prefix+decoded") {{decoded}}.

        template(v-if="recoveryNotPossible")
            v-alert(type="warning" icon="fas fa-exclamation-triangle" :dense="$vuetify.breakpoint.xs")
                p {{$t('passwordRecovery.request.userNotFoundMessage')}}
                p {{$t('passwordRecovery.request.checkInputAndTryAgainMessage')}}
                p {{$t('passwordRecovery.request.contactSupportMessage')}}
            v-card-actions
                v-btn(color="primary" width="100%" :height="buttonHeight" @click="onRepeatButtonClick") {{$t('passwordRecovery.request.tryAgainButtonLabel')}}

        template(v-if="noEmailForRecovery")
            v-alert(type="warning" icon="fas fa-exclamation-triangle" :dense="$vuetify.breakpoint.xs")
                div(v-html="$t('passwordRecovery.request.noEmailMessage')")
            v-card-actions
                v-btn(color="primary" width="100%" :height="buttonHeight" @click="onAcknowledgeButtonClick") {{$t('common.acknowledgeButtonLabel')}}

        template(v-if="isRequestSuccessful")
            v-alert(type="info" icon="fas fa-info-circle" :dense="$vuetify.breakpoint.xs") {{$t('passwordRecovery.request.requestSuccessMessage')}}
            v-card-actions
                v-btn(color="primary" width="100%" :height="buttonHeight" @click="onAcknowledgeButtonClick") {{$t('common.acknowledgeButtonLabel')}}

        template(v-if="showForm")
            v-card-text.pb-0
                v-text-field(
                    v-model="userIdentifier" :label="$t('loginPage.loginNameOrEmail')" outlined clearable clear-icon="fal fa-times"
                    :rules="userIdentifierValidationRules"
                )

            v-card-actions.px-4.py-0
                v-row
                    v-col(cols="12")
                        v-btn(color="primary" :disabled="!isFormValid" type="submit" width="100%" :height="buttonHeight" :loading="isRequestInProgress") {{$t('passwordRecovery.request.submitButtonLabel')}}

</template>

<script lang="ts">
import Vue from 'vue'
import {AUTH_REQUEST_PASSWORD_RECOVERY} from '@/store/action-types'
import {PasswordRecoveryRequestPayload} from '@/types/payload'
import {Component, Watch} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import {validateEmail, valueIsRequired} from '@/services/validation'
import UserDoesNotHaveAnyEmail from '@/model/error/UserDoesNotHaveAnyEmail'

@Component({})
export default class RecoveryRequestForm extends Vue {
  $refs!: {
    form: Vue & { validate(): boolean, resetValidation(): void }
  }

  private userIdentifier: string | null = null
  private isRequestInProgress: boolean = false
  private isFormValid: boolean = false
  private recoveryNotPossible: boolean = false
  private noEmailForRecovery: boolean = false
  private isRequestSuccessful: boolean = false
  private userIdentifierValidationRules: Function[] = [valueIsRequired]
  private decoded: string = atob("cG9kcG9yYUBwb3pyaW1lbnUuc2s=")
  private prefix: string = atob("bWFpbHRvOg==")

  get buttonHeight(): number | undefined {
    if (this.$vuetify.breakpoint.smAndDown) {
      return 64
    }
  }

  get showForm(): boolean {
    return !this.noEmailForRecovery && !this.recoveryNotPossible && !this.isRequestSuccessful
  }

  get showTextInstructions(): boolean {
    return this.showForm
  }

  @Action(AUTH_REQUEST_PASSWORD_RECOVERY) requestPasswordRecovery!: { (payload: PasswordRecoveryRequestPayload): Promise<void> }

  resetState(): void {
    this.isRequestSuccessful = false
    this.noEmailForRecovery = false
    this.recoveryNotPossible = false
  }

  async onFormSubmit(): Promise<void> {
    if (this.$refs.form.validate()) {
      this.resetState()

      this.isRequestInProgress = true

      try {
        const payload: PasswordRecoveryRequestPayload = {
          loginName: validateEmail(this.userIdentifier!) !== true ? this.userIdentifier : null,
          email:  validateEmail(this.userIdentifier!) === true ? this.userIdentifier : null
        }

        await this.requestPasswordRecovery(payload)

        this.isRequestSuccessful = true
      } catch (e) {
        if (e instanceof UserDoesNotHaveAnyEmail) {
          this.noEmailForRecovery = true
        } else {
          this.recoveryNotPossible = true
        }
      } finally {
        this.isRequestInProgress = false
      }
    }
  }

  onAcknowledgeButtonClick(): void {
    this.$router.replace({name: 'Login'})
  }

  onRepeatButtonClick(): void {
    this.resetState()
  }
}
</script>

<style lang="stylus" scoped>
.card-envelope
  max-width 600px
</style>
